import type {
  ProcessedTool,
  ToolkitCollection,
} from "@arcadeai/ui-kit/arcade/arcade";
import type { McpServer } from "@arcadeai/ui-kit/mcp-server-picker";

/**
 * Map the dashboard's toolkit catalog into the shape McpServerPicker renders.
 *
 * Selection identity in the picker is the version-less qualified name
 * ("Toolkit.Tool"), which already matches what the gateway stores in
 * `tool_filter.allowed_tools`, so no FQN reconciliation is needed — the
 * picker's `selectedFqns`/`onChange` bind straight to that field.
 */
export function toolkitCollectionToMcpServers(
  collection: ToolkitCollection
): McpServer[] {
  return collection.toolkits.map((toolkit) => ({
    name: toolkit.name,
    displayName: toolkit.metadata?.label ?? toolkit.name,
    description: toolkit.description,
    // Only the catalog's own starters are "unoptimized"; everything else
    // (including user-added toolkits with no metadata) shows as first-class.
    isUnoptimized: toolkit.metadata?.type === "arcade_starter",
    version: toolkit.version,
    tools: toolkit.tools,
  }));
}

/**
 * Index the catalog's tools by their lower-cased version-less qualified name, so
 * a stored allow-list can be resolved case-insensitively (stored names may carry
 * legacy or differently-cased entries).
 */
export function indexToolsByLowerQualifiedName(
  collection: ToolkitCollection
): Map<string, ProcessedTool> {
  return new Map(
    collection.tools.map((tool) => [tool.qualified_name.toLowerCase(), tool])
  );
}

/**
 * Count the distinct stored names that resolve to no catalog tool (version- and
 * case-insensitive) — the number of genuinely dropped tools. Unlike an array-
 * length delta it is unaffected by entries that merely collapse together during
 * de-duplication, so a warning only fires for tools truly unavailable here.
 */
export function countUnavailableTools(
  toolIndex: Map<string, ProcessedTool>,
  allowedTools: readonly string[]
): number {
  const unavailable = new Set<string>();
  for (const name of allowedTools) {
    const key = name.split("@")[0].toLowerCase();
    if (!toolIndex.has(key)) {
      unavailable.add(key);
    }
  }
  return unavailable.size;
}

/**
 * Resolve stored allow-list names against the catalog index, returning the
 * matching catalog tools (with canonical casing). Any version suffix is stripped
 * before lookup so a legacy versioned entry ("Toolkit.Tool@1.0.0") still maps to
 * its version-less catalog tool. Names absent from the catalog are dropped — the
 * picker can't represent tools it doesn't know about.
 *
 * Entries that collapse to the same catalog tool (e.g. differently-cased or
 * multiple versioned forms of one tool, or a doubled `?tools=` deep link) are
 * de-duplicated to their first occurrence, so the reconciled allow-list never
 * carries the same tool twice.
 */
export function resolveSelectedTools(
  toolIndex: Map<string, ProcessedTool>,
  allowedTools: readonly string[]
): ProcessedTool[] {
  const seen = new Set<string>();
  const resolved: ProcessedTool[] = [];
  for (const name of allowedTools) {
    const tool = toolIndex.get(name.split("@")[0].toLowerCase());
    if (!tool || seen.has(tool.qualified_name)) {
      continue;
    }
    seen.add(tool.qualified_name);
    resolved.push(tool);
  }
  return resolved;
}
